import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Clock3 } from "lucide-react";
import { cancelMembershipPayment, listTenantMembershipPayments, payMembershipPayment } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import { newIdempotencyKey } from "../lib/api/idempotency";
import { formatDate, money } from "../lib/format";
import { MEMBERSHIP_PAYMENT_STATUS, membershipPaymentStatusLabel, paymentMethodLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { TenantMembershipPaymentDto } from "../types";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Card } from "./Card";
import { Label } from "./Label";
import { Select } from "./Select";
import { ConfirmDialog } from "./ConfirmDialog";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: String(MEMBERSHIP_PAYMENT_STATUS.Pending), label: "Pendentes" },
  { value: String(MEMBERSHIP_PAYMENT_STATUS.Paid), label: "Pagas" },
  { value: String(MEMBERSHIP_PAYMENT_STATUS.Cancelled), label: "Canceladas" },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "1", label: "Dinheiro" },
  { value: "2", label: "Pix" },
  { value: "3", label: "Cartão de crédito" },
  { value: "4", label: "Cartão de débito" },
];

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.detail ?? error.message : "Falha ao carregar mensalidades.";
}

function paymentBadge(payment: TenantMembershipPaymentDto) {
  if (payment.status === MEMBERSHIP_PAYMENT_STATUS.Paid) {
    return <Badge bg={C.limeSoft} color={C.ink}><CheckCircle2 size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{membershipPaymentStatusLabel(payment.status)}</Badge>;
  }
  if (payment.status === MEMBERSHIP_PAYMENT_STATUS.Cancelled) {
    return <Badge bg={C.bg} color={C.gray}>{membershipPaymentStatusLabel(payment.status)}</Badge>;
  }
  if (payment.isOverdue) {
    return <Badge bg={C.redSoft} color={C.red}><AlertCircle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Em atraso</Badge>;
  }
  return <Badge bg={C.bg} color={C.gray}><Clock3 size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Pendente</Badge>;
}

function dueDescription(payment: TenantMembershipPaymentDto): string {
  if (payment.isOverdue) return `${payment.daysOverdue} ${payment.daysOverdue === 1 ? "dia" : "dias"} em atraso`;
  if (payment.status === MEMBERSHIP_PAYMENT_STATUS.Pending && payment.daysUntilDue > 0) return `vence em ${payment.daysUntilDue} ${payment.daysUntilDue === 1 ? "dia" : "dias"}`;
  return "vence hoje";
}

export function MembershipPaymentsTab() {
  const { accessToken } = useSession();
  const token = accessToken ?? "";
  const [payments, setPayments] = useState<TenantMembershipPaymentDto[] | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [payingPayment, setPayingPayment] = useState<TenantMembershipPaymentDto | null>(null);
  const [cancellingPayment, setCancellingPayment] = useState<TenantMembershipPaymentDto | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("2");
  const [processing, setProcessing] = useState(false);
  const mutationRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setPayments(null);
    setLoadError(null);
    listTenantMembershipPayments(token, { page, pageSize: PAGE_SIZE, status: statusFilter ? Number(statusFilter) : undefined })
      .then(result => {
        if (cancelled) return;
        setPayments(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(error => {
        if (cancelled) return;
        setPayments([]);
        setLoadError(errorMessage(error));
      });
    return () => { cancelled = true; };
  }, [token, page, statusFilter, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const reload = () => setReloadKey(value => value + 1);

  const confirmPay = async () => {
    if (!token || !payingPayment || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await payMembershipPayment(token, payingPayment.id, { paymentMethod: Number(paymentMethod) }, newIdempotencyKey());
      setPayingPayment(null);
      reload();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const confirmCancel = async () => {
    if (!token || !cancellingPayment || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await cancelMembershipPayment(token, cancellingPayment.id, newIdempotencyKey());
      setCancellingPayment(null);
      reload();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <Label>Controle de mensalidades</Label>
          <div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Acompanhe os vencimentos e pagamentos dos alunos.</div>
        </div>
        <Select ariaLabel="Filtrar mensalidades por status" value={statusFilter} options={STATUS_OPTIONS} onChange={value => { setStatusFilter(value); setPage(1); }} style={{ minWidth: 190 }} />
      </div>

      {loadError && <div role="alert" style={{ color: C.red, background: C.redSoft, borderRadius: 10, padding: 12, marginTop: 14, fontSize: 12.5 }}>{loadError} <Button variant="ghost" onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</Button></div>}
      {payments === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando mensalidades...</div>}
      {payments?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhuma mensalidade encontrada.</div>}

      {payments && payments.length > 0 && <div style={{ display: "grid", gap: 8, marginTop: 14 }}>{payments.map(payment => (
        <div key={payment.id} style={{ border: `1px solid ${payment.isOverdue ? C.redSoft : C.faint}`, borderRadius: 10, padding: 12, display: "grid", gridTemplateColumns: "minmax(180px, 1.4fr) minmax(110px, .7fr) minmax(120px, .8fr) minmax(135px, 1fr) auto", gap: 12, alignItems: "center" }}>
          <div style={{ display: "grid", gap: 5 }}>
            <b style={{ color: C.ink, fontSize: 13.5 }}>{payment.memberName}</b>
            {paymentBadge(payment)}
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <span style={{ color: C.light, fontSize: 11.5 }}>Valor</span>
            <b style={{ color: C.ink, fontSize: 13 }}>{money(payment.amount)}</b>
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <span style={{ color: C.light, fontSize: 11.5 }}>{payment.status === MEMBERSHIP_PAYMENT_STATUS.Paid ? "Pago em" : "Vencimento"}</span>
            <span style={{ color: C.gray, fontSize: 12.5 }}>{payment.status === MEMBERSHIP_PAYMENT_STATUS.Paid && payment.paidAt ? formatDate(payment.paidAt) : formatDate(payment.dueDate)}</span>
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <span style={{ color: C.light, fontSize: 11.5 }}>{payment.status === MEMBERSHIP_PAYMENT_STATUS.Paid ? "Forma de pagamento" : "Situação"}</span>
            <span style={{ color: payment.isOverdue ? C.red : C.gray, fontSize: 12.5, fontWeight: payment.isOverdue ? 700 : 400 }}>{payment.status === MEMBERSHIP_PAYMENT_STATUS.Paid && payment.paymentMethod ? paymentMethodLabel(payment.paymentMethod) : dueDescription(payment)}</span>
          </div>
          {payment.status === MEMBERSHIP_PAYMENT_STATUS.Pending && <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => { setActionError(null); setPaymentMethod("2"); setPayingPayment(payment); }}>Pagar</Button>
            <Button variant="ghost" ariaLabel={`Cancelar mensalidade de ${payment.memberName}`} onClick={() => { setActionError(null); setCancellingPayment(payment); }}>Cancelar</Button>
          </div>}
        </div>
      ))}</div>}

      {totalCount > PAGE_SIZE && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, alignItems: "center" }}>
        <span style={{ color: C.light, fontSize: 12 }}>Página {page} de {totalPages}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Anterior</Button>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>Próxima</Button>
        </div>
      </div>}

      {payingPayment && <div role="dialog" aria-modal="true" aria-labelledby="membership-pay-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
        <Card style={{ width: "100%", maxWidth: 420 }}>
          <div id="membership-pay-title"><Label>Registrar pagamento</Label></div>
          <p style={{ color: C.gray, fontSize: 13, lineHeight: 1.5, margin: "12px 0 16px" }}>Confirme o pagamento de <b style={{ color: C.ink }}>{payingPayment.memberName}</b> no valor de {money(payingPayment.amount)}.</p>
          <label htmlFor="membership-payment-method" style={{ display: "block", color: C.gray, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Forma de pagamento</label>
          <Select id="membership-payment-method" ariaLabel="Forma de pagamento" value={paymentMethod} options={PAYMENT_METHOD_OPTIONS} disabled={processing} onChange={setPaymentMethod} />
          {actionError && <div role="alert" style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{actionError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" disabled={processing} onClick={() => setPayingPayment(null)}>Cancelar</Button>
            <Button disabled={processing} onClick={confirmPay}>{processing ? "Salvando..." : "Confirmar pagamento"}</Button>
          </div>
        </Card>
      </div>}

      {cancellingPayment && <ConfirmDialog title="Cancelar mensalidade" confirmLabel="Cancelar mensalidade" destructive busy={processing} onConfirm={confirmCancel} onCancel={() => !processing && setCancellingPayment(null)}>
        Deseja cancelar a mensalidade de <b style={{ color: C.ink }}>{cancellingPayment.memberName}</b> no valor de {money(cancellingPayment.amount)}?
        {actionError && <span role="alert" style={{ display: "block", color: C.red, marginTop: 8 }}>{actionError}</span>}
      </ConfirmDialog>}
    </Card>
  );
}
